'use client';

import type { AnalyticsItemType } from 'src/types/analytics/events';

import { useEffect, useCallback } from 'react';

import { track } from 'src/lib/analytics/client';

import type { MediaCard } from '../../utils/search-all-media-cards';

/** 상세에서 허브로 건너뛰는 문 — `cta_clicked.cta_id`. 어느 화면의 상세인지는 봉투의 화면이 가린다 */
export const MEDIA_DETAIL_CTA = {
  creatorHub: 'media-detail.head.creator-hub',
  brandHub: 'media-detail.head.brand-hub',
  brandTag: 'media-detail.tags.brand-hub',
} as const;

export type MediaDetailCta = (typeof MEDIA_DETAIL_CTA)[keyof typeof MEDIA_DETAIL_CTA];

const ITEM_TYPE: Record<MediaCard['kind'], AnalyticsItemType> = { organic: 'post', ad: 'ad', product: 'product' };

/**
 * 카드가 무엇인가 — `item_opened` 의 종류와 id.
 * @returns 종류(글 `post` · 광고 `ad` · 상품 `product`)와 카드 key 에서 갈래 머리를 뗀 id(상품은 `몰:상품 id`)
 */
export function mediaItemOf(card: Pick<MediaCard, 'key' | 'kind'>): { item_type: AnalyticsItemType; item_id: string } {
  const at = card.key.indexOf(':');
  return { item_type: ITEM_TYPE[card.kind], item_id: at >= 0 ? card.key.slice(at + 1) : card.key };
}

/**
 * 소재 상세의 활동 로그 — 상세가 카드를 **보일 때마다** `item_opened` 한 줄, 허브로 건너뛰면 `cta_clicked` 한 줄.
 *
 * 🔑 카드 key 가 바뀔 때 센다 — 처음 열기 · 이전/다음 · 비슷한 소재로 갈아 끼우기가 다 한 줄씩이다.
 *   닫았다가 같은 카드를 다시 열어도 한 줄이다(사이에 `null` 이 낀다).
 * ⚠️ `enabled` 가 거짓이면 아무것도 남기지 않는다 — 사용 가이드의 체험 화면처럼 실사용이 아닌 자리다.
 * @param card 지금 보이는 카드 — 닫혀 있으면 `null`
 * @param enabled 남기는가
 * @returns 허브 문을 눌렀을 때 부르는 기록 함수(`cta_id` · 표시 이름)
 */
export function useMediaDetailTrack(card: MediaCard | null, enabled: boolean): (ctaId: MediaDetailCta, label?: string) => void {
  const key = card?.key ?? '';
  const kind = card?.kind;
  useEffect(() => {
    if (!enabled || !key || !kind) return;
    track({ name: 'item_opened', type: 'action', props: mediaItemOf({ key, kind }) }, { uiId: 'media-detail.dialog.item' });
  }, [enabled, key, kind]);

  return useCallback(
    (ctaId: MediaDetailCta, label?: string) => {
      if (!enabled) return;
      track({ name: 'cta_clicked', type: 'action', props: { cta_id: ctaId, ...(label ? { label } : {}) } }, { uiId: ctaId });
    },
    [enabled],
  );
}
